/**
 * 勇者成長ユーティリティ
 * ポイントをレベル・クラス表示へ変換します
 */

import HERO_STAGE_IMAGE_LEVEL_1 from '../../../../assets/勇者/かけだし冒険者.png';
import HERO_STAGE_IMAGE_LEVEL_2 from '../../../../assets/勇者/はじまりの旅人.png';
import HERO_STAGE_IMAGE_LEVEL_3 from '../../../../assets/勇者/疾風の剣士.png';
import HERO_STAGE_IMAGE_LEVEL_4 from '../../../../assets/勇者/颯の英雄.png';
import {
  DEFAULT_HERO_RANKS,
  normalizeHeroRanks,
  type PublicHeroRank,
} from '../../../shared/config/appPublicSettings';

/** 勇者ランクの既定表示装飾 */
const HERO_STAGE_PRESENTATION_LIST = Object.freeze([
  {
    auraClassName: 'bg-app-quest-cloud',
    imageSource: HERO_STAGE_IMAGE_LEVEL_2,
  },
  {
    auraClassName: 'bg-app-secondary',
    imageSource: HERO_STAGE_IMAGE_LEVEL_1,
  },
  {
    auraClassName: 'bg-app-primary-container',
    imageSource: HERO_STAGE_IMAGE_LEVEL_3,
  },
  {
    auraClassName: 'bg-app-quest-gold/25',
    imageSource: HERO_STAGE_IMAGE_LEVEL_4,
  },
]);

/**
 * 公開設定から表示用勇者ステージを作る
 * @param {readonly PublicHeroRank[] | undefined} heroRanks - 公開勇者ランク
 * @returns {Array<{ rankId: string, level: number, minPoint: number, title: string, auraClassName: string, imageSource: any }>} 表示用ステージ
 */
const buildHeroStageList = (
  heroRanks?: readonly PublicHeroRank[],
) => {
  const normalizedHeroRanks = normalizeHeroRanks(
    Array.isArray(heroRanks) ? heroRanks : DEFAULT_HERO_RANKS,
  );

  return normalizedHeroRanks.map((heroRank, index) => {
    const presentation =
      HERO_STAGE_PRESENTATION_LIST[index]
      ?? HERO_STAGE_PRESENTATION_LIST[HERO_STAGE_PRESENTATION_LIST.length - 1];

    return {
      rankId: heroRank.rankId,
      level: index + 1,
      minPoint: heroRank.minPoints,
      title: heroRank.name,
      auraClassName: presentation.auraClassName,
      imageSource: heroRank.imageUrl
        ? { uri: heroRank.imageUrl }
        : presentation.imageSource,
    };
  });
};

/**
 * 勇者ランク判定用EXPを整数へ正規化する
 * @param {number | string | null | undefined} exp - EXP
 * @returns {number} 正規化済みEXP
 */
const normalizeHeroExp = (exp) => {
  /** 数値化したEXP */
  const numericExp = Number(exp);

  if (!Number.isFinite(numericExp)) {
    return 0;
  }

  return Math.max(0, Math.floor(numericExp));
};

/**
 * 友達から受け取った現在ptを混ぜず、DBの累計獲得ptだけをランク判定に使う。
 * @param {number} _currentPoint - 既存呼出し互換の現在ポイント（判定には使わない）
 * @param {number} maxReachedExp - 累計獲得pt
 * @returns {number} ランク判定用EXP
 */
export const resolveHeroRankPoint = (_currentPoint, maxReachedExp) => {
  /** 累計獲得pt */
  const normalizedMaxReachedExp = normalizeHeroExp(maxReachedExp);

  return normalizedMaxReachedExp;
};

/**
 * ポイントから現在ステージを返す
 * @param {number} point - 現在ポイント
 * @param {readonly PublicHeroRank[]} [heroRanks] - 公開勇者ランク
 * @returns {{ rankId: string, level: number, minPoint: number, title: string, auraClassName: string, imageSource: any }} ステージ情報
 */
export const resolveHeroStage = (
  point,
  heroRanks?: readonly PublicHeroRank[],
) => {
  /** 正規化済みポイント */
  const normalizedPoint = normalizeHeroExp(point);

  /** 表示用ステージ */
  const heroStageList = buildHeroStageList(heroRanks);

  return heroStageList.reduce((currentStage, stage) => {
    if (normalizedPoint >= stage.minPoint) {
      return stage;
    }

    return currentStage;
  }, heroStageList[0]);
};

/**
 * 次ステージを返す
 * @param {number} point - 現在ポイント
 * @param {readonly PublicHeroRank[]} [heroRanks] - 公開勇者ランク
 * @returns {{ rankId: string, level: number, minPoint: number, title: string, auraClassName: string, imageSource: any } | null} 次ステージ
 */
export const resolveNextHeroStage = (
  point,
  heroRanks?: readonly PublicHeroRank[],
) => {
  /** 現在ステージ */
  const currentStage = resolveHeroStage(point, heroRanks);

  /** 表示用ステージ */
  const heroStageList = buildHeroStageList(heroRanks);

  return heroStageList.find(
    (stage) => stage.level === currentStage.level + 1,
  ) ?? null;
};

/**
 * 現在ステージ内での進捗率を返す
 * @param {number} point - 現在ポイント
 * @param {readonly PublicHeroRank[]} [heroRanks] - 公開勇者ランク
 * @returns {number} 0-1 の進捗率
 */
export const resolveHeroProgressRatio = (
  point,
  heroRanks?: readonly PublicHeroRank[],
) => {
  /** 正規化済みポイント */
  const normalizedPoint = normalizeHeroExp(point);

  /** 現在ステージ */
  const currentStage = resolveHeroStage(normalizedPoint, heroRanks);

  /** 次ステージ */
  const nextStage = resolveNextHeroStage(normalizedPoint, heroRanks);

  if (!nextStage) {
    return 1;
  }

  /** 必要ポイント差分 */
  const rangePoint = Math.max(1, nextStage.minPoint - currentStage.minPoint);

  /** 現在ステージでの獲得ポイント */
  const progressedPoint = Math.max(0, normalizedPoint - currentStage.minPoint);

  return Math.min(1, progressedPoint / rangePoint);
};

/**
 * レベルを表示用文字列に変換する
 * @param {number} point - 現在ポイント
 * @param {readonly PublicHeroRank[]} [heroRanks] - 公開勇者ランク
 * @returns {string} 表示用レベル文字列
 */
export const resolveHeroLevelLabel = (
  point,
  heroRanks?: readonly PublicHeroRank[],
) => {
  return `Lv.${resolveHeroStage(point, heroRanks).level}`;
};
